import { ExcelList, List } from "../../services/transaction.services";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DatePicker } from "rsuite";
import { capitalizeFirstLetter, currencyCovMap, showFormattedDate, writeMyExcel } from "../../../../utils/commonfunction";
import Invoice from "../../../../utils/transactionPdf";

const CalendarCaret = () => <i className="fi fi-rr-calendar calendar-icon-inside" />;

export default function BusinessTransactions() {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ name: null, date: null });
  const [openPopupIndex, setOpenPopupIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const popupRef = useRef(null);
  const receiptRef = useRef();
  const [downloadReport, setDownloadReport] = useState(false);


  function useDebounce(value, delay = 500) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const timer = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(timer);
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedFilters = useDebounce(filters, 600);

  const fetchTransactions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await List({
        ...debouncedFilters,
        page,
        limit: 10,
      });

      setTransactions(response.data?.list || []);
      setCurrentPage(response.data?.page || 1);
      setTotalPages(Math.ceil(response.data?.total_records / 10) || 1);
    } catch (error) {
      console.error("Error fetching transactions", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOpenPopupIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1); // reset to first page when filters change
    fetchTransactions(1);
  }, [debouncedFilters]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchTransactions(page);
    }
  };

  const resetFilter = () => {
    setFilters({ name: null, date: null });

  };

  const handleExportToExcel = () => {
    setDownloadReport(true);
    ExcelList()
      .then((data) => {
        setDownloadReport(false);
        const headers = [
          "Date",
          "Customer Email",
          "Offer Used",
          "Amount",
          "Type",
          "Status",
          "Reference ID",
        ];

        const rows = data.data.list.map((txn) => {
          const user = txn.user?.[0] || {};
          const currencySymbol = currencyCovMap[txn.currency?.toUpperCase()] || txn.currency?.toUpperCase() || '';
          const customerEmail = user?.email || txn.userEmail || (txn.userName?.includes("@") ? txn.userName : null) || user?.fullName || txn.userName || "N/A";

          return [
            txn.createdAt ? new Date(txn.createdAt).toLocaleDateString() : "N/A",
            customerEmail,
            txn.offerPercentage ? `${txn.offerPercentage}%` : "N/A",
            `${currencySymbol} ${txn.totalAmount ?? 0}`,
            txn.paymentMethod ?? "N/A",
            txn.status ?? "N/A",
            txn.transactionID ?? "N/A",
          ];
        });
        writeMyExcel([headers, ...rows], `${t('Transaction list')}.xlsx`);
      })
      .catch((error) => {
        setDownloadReport(false);
        console.log("error ====> ", error);
      });
  };

  return (
    <>
      <div className="main-content">
        <div className="customer-part">
          <div className="card-box">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 w-100 mb-4">
              <div className="search-bar d-flex flex-wrap align-items-center gap-2">
                <div className="search-input">
                  <input type="text" className="form-control" placeholder="Search by name or email" value={filters?.name || ""} onChange={(e) => setFilters({ ...filters, name: e.target.value })} />
                  <i className="fi fi-rs-search" />
                </div>

                <div className="date-input">
                  <DatePicker
                    value={filters.date ? new Date(filters.date) : null}
                    onChange={(date) => {
                      setFilters({ ...filters, date: date });
                    }}
                    format="yyyy-MM-dd"
                    placeholder="Select date"
                    cleanable={true}
                    oneTap={true}
                    caretAs={CalendarCaret}
                    className="custom-date-picker"
                  />
                </div>

                <button type="reset" value="Reset" onClick={() => (resetFilter())} className="btn btn-oring ripple">
                  {t("btn_reset_filter")}
                </button>
              </div>
              <button onClick={handleExportToExcel} disabled={downloadReport} className="btn btn-oring text-nowrap">Export report</button>
            </div>

            {/* Transactions Table */}
            <div className="table-box table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer Email</th>
                    <th>Offer Used</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Reference ID</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8">Loading...</td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr className="text-center">
                      <td className="m-2" colSpan="8">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((txn, index) => {
                      const currency = (txn.currency || "GBP").toUpperCase();
                      const currSymbol = currency === "GBP" ? "£" : (currencyCovMap[currency] ?? "$");
                      const customerEmailDisplay = txn?.user?.[0]?.email || txn?.userEmail || (txn?.userName?.includes("@") ? txn?.userName : null) || txn?.user?.[0]?.fullName || txn?.userName || "N/A";
                      const netPayNum = Number(txn?.netPay !== undefined && txn?.netPay !== null ? txn.netPay : (txn?.totalAmount || 0));
                      const totalAmtNum = Number(txn?.totalAmount || 0);
                      const discAmtNum = Number(txn?.discountAmount || 0);

                      const formattedData = {
                        userName: txn?.userName || "NA",
                        transactionID: txn?.transactionID || "NA",
                        billingDate: showFormattedDate(txn?.createdAt) || "NA",
                        customerName: customerEmailDisplay,
                        type: txn?.paymentMethod || "NA",
                        status: txn?.status || "NA",

                        discountAmount: `${currSymbol} ${discAmtNum.toFixed(2)}`,
                        totalAmount: `${currSymbol} ${totalAmtNum.toFixed(2)}`,
                        netPay: `${currSymbol} ${netPayNum.toFixed(2)}`,

                        offerName: txn?.offer?.[0]?.name || txn?.offer?.[0]?.title || (txn.offerPercentage ? `${txn.offerPercentage}%` : "NA"),
                        currency,
                      };
                      return (
                        <tr key={index}>
                          <td>
                            {new Date(txn.createdAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </td>
                          <td>{customerEmailDisplay}</td>
                          <td>{txn.offer?.[0]?.name || txn.offer?.[0]?.title || (txn.offerPercentage ? `${txn.offerPercentage}%` : "N/A")}</td>
                          <td>{`${currSymbol} ${netPayNum.toFixed(2)}`}</td>
                          <td>{txn.paymentMethod || "card"}</td>
                          <td>
                            <span className="badge">{txn.status}</span>
                          </td>
                          <td>{txn.transactionID}</td>
                          <td className="popup">
                            <button className="btn btn-sm toggle-popup" onClick={() => setOpenPopupIndex(index)}>
                              <i className="fi fi-rr-menu-dots-vertical" />
                            </button>
                            {openPopupIndex === index && (
                              <div className={"popup-box z-50"} ref={popupRef}>
                                <ul>
                                  <li>
                                    <i className="fi fi-rr-eye" />
                                    <Link to={`/business/transactions/${txn._id}`}>View Details</Link>
                                  </li>
                                  <li>
                                    <i className="fi fi-sr-download" />

                                    {/* Hidden wrapper to hold the Invoice */}
                                    <div style={{ display: "none" }}>
                                      <Invoice ref={receiptRef} transactionData={formattedData} />
                                    </div>

                                    {/* Visible link to trigger action */}
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        // console.log("got clicked");
                                        receiptRef.current?.download();

                                      }}
                                    >
                                      Download Receipt
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="pagination d-flex justify-content-center mt-3">
              <button className="btn btn-light me-2" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                Prev
              </button>
              <span className="align-self-center">
                Page {currentPage} of {totalPages}
              </span>
              <button className="btn btn-light ms-2" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
